import React, { useMemo } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { merge } from 'lodash';
import { useRouter } from 'next/router';
import { vars } from '$util/theme';
import { CmsLinkSchema, CmsNumberSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import type { ContentComponent } from '$cms/ContentComponent';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import type { ComparisonCardProps } from '$quantum/molecules/ComparisonCard';
import { ComparisonCard } from '$quantum/molecules/ComparisonCard';
import type { ReviewProvider } from '$constants/review-providers';
import { Col, Row } from '@/components/atoms/Grid';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import styles from './index.module.scss';

const defaultConfiguration = {
  backgroundColor: vars.quantumverse.reskinColors.purple200,
};

const ComparisonCardsSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    subheading: CmsShortTextSchema,
    cardTitle: z.record(z.string(), CmsShortTextSchema),
    cardTitleTag: z.record(z.string(), CmsShortTextSchema).optional(),
    cardSubtitle: z.record(z.string(), CmsShortTextSchema),
    cardSubtitlePrice: z.record(z.string(), CmsShortTextSchema),
    cardCTA: z.record(z.string(), CmsLinkSchema),
    cardMetaTitle: z.record(z.string(), CmsShortTextSchema).optional(),
    cardMetaSubTitle: z.record(z.string(), z.union([CmsLinkSchema, CmsShortTextSchema])).optional(),
    cardContent: z.record(z.string(), CmsRichTextSchema),
    cardRating: z.record(z.string(), CmsNumberSchema),
    cardReviewProvider: z.record(z.string(), CmsShortTextSchema),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
    })
    .default(defaultConfiguration),
};

export type ComparisonCardsTranslatedEntities = SchemaType<
  (typeof ComparisonCardsSchema)['translatedEntities']
>;
export type ComparisonCardsConfiguration = SchemaType<(typeof ComparisonCardsSchema)['configuration']>;

/**
 * A content component that always displays Comparison Cards.<br />
 * https://www.notion.so/fixter/ComparisonCards-c8f105de6f004ffc9f1c64b9eed22c16
 */
export const ComparisonCards: ContentComponent<
  ComparisonCardsTranslatedEntities,
  ComparisonCardsConfiguration
> = function ComparisonCards({ translatedEntities, configuration, className }): JSX.Element {
  const {
    cardTitle,
    cardSubtitle,
    cardCTA,
    cardContent,
    cardSubtitlePrice,
    cardMetaSubTitle,
    cardMetaTitle,
    cardTitleTag,
    heading,
    subheading,
    cardRating,
    cardReviewProvider,
  } = translatedEntities;
  const { backgroundColor } = merge(defaultConfiguration, configuration);
  const { push } = useRouter();

  const comparisonCardsProps = useMemo(() => {
    const cardsKeys = Object.keys(cardTitle);

    return cardsKeys.map((cardKey) => {
      const props: ComparisonCardProps = {
        title: cardTitle[cardKey].value,
        subtitle: cardSubtitle[cardKey].value,
        subtitlePrice: cardSubtitlePrice[cardKey].value,
        content: cardContent[cardKey].content,
        ctaText: cardCTA[cardKey].text,
        ctaHandler: () => push(cardCTA[cardKey].url, undefined, { shallow: true, scroll: true }),
        metaTitle: cardMetaTitle?.[cardKey].value,
        metaSubTitle: cardMetaSubTitle?.[cardKey],
        titleTag: cardTitleTag?.[cardKey]?.value,
        rating: cardRating?.[cardKey].number,
        reviewProvider: cardReviewProvider?.[cardKey].value as ReviewProvider,
      };

      return props;
    });
  }, [
    cardTitle,
    cardSubtitle,
    cardCTA,
    cardContent,
    cardSubtitlePrice,
    cardMetaSubTitle,
    cardMetaTitle,
    cardTitleTag,
    cardRating,
    cardReviewProvider,
    push,
  ]);

  return (
    <ContentComponentSection
      className={classNames(ComparisonCards.displayName, styles.ComparisonCards, className)}
      data-background={backgroundColor}
      data-cards-length={comparisonCardsProps.length}
    >
      <Row>
        <Col>
          <HeadingLevels className={styles.Heading} semanticLevel={2}>
            {heading.value}
          </HeadingLevels>
          {subheading && (
            <HeadingLevels className={styles.Subheading} semanticLevel={4}>
              {subheading.value}
            </HeadingLevels>
          )}
          <div className={styles.comparisonCardsWrapper}>
            {comparisonCardsProps.map((props) => (
              <ComparisonCard key={props.title} {...props} />
            ))}
          </div>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};

ComparisonCards.displayName = 'ComparisonCards';
ComparisonCards.schema = ComparisonCardsSchema;
